import { useEffect, useState } from "react";
import { api, imgSrc } from "@/lib/api";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { GALLERY_FILTERS } from "@/data/static";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("All");
  const [zoom, setZoom] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gallery", { params: { type: "general" } })
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filtered = active === "All" ? items : items.filter((i) => i.category === active);

  return (
    <div data-testid="gallery-page" className="pt-32 pb-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <MaskedLine delay={0.1}><p className="overline-label">Gallery</p></MaskedLine>
        <h1 className="mt-4 font-cinzel font-black text-brand-burgundy text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
          <MaskedLine delay={0.25}>MOMENTS OF</MaskedLine>
          <MaskedLine delay={0.4} innerClassName="text-gold-gradient">BEAUTY.</MaskedLine>
        </h1>
        <MaskedLine delay={0.55} className="mt-6">
          <p className="max-w-2xl text-base leading-relaxed text-brand-ink-muted">
            Potret suasana salon, klinik kecantikan, body massage, hijab room, kids, dan cafe yang dirancang untuk kenyamanan dan privasi tamu.
          </p>
        </MaskedLine>

        <div className="mt-12 flex flex-wrap gap-2.5" data-testid="gallery-filters">
          {GALLERY_FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`gallery-filter-${f.toLowerCase()}`}
              onClick={() => setActive(f)}
              className={`rounded-full px-5 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] transition-all duration-300 ${
                active === f
                  ? "bg-brand-burgundy text-brand-cream shadow-[0_8px_25px_rgba(74,14,23,0.3)]"
                  : "bg-white text-brand-ink-muted gold-foil-border hover:text-brand-burgundy hover:border-brand-gold"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-20 text-center text-brand-ink-muted" data-testid="gallery-loading">Memuat galeri...</p>
        ) : filtered.length === 0 ? (
          <div className="mt-20 rounded-sm gold-foil-border bg-brand-paper p-16 text-center" data-testid="gallery-empty">
            <p className="font-serif text-xl text-brand-burgundy">Belum ada foto di kategori ini</p>
            <p className="mt-2 text-sm text-brand-ink-muted">Foto akan segera diunggah oleh tim kami.</p>
          </div>
        ) : (
          <div className="mt-12 columns-2 gap-5 md:columns-3 [&>div]:mb-5" data-testid="gallery-masonry">
            {filtered.map((item, i) => (
              <Reveal key={item.id} delay={(i % 3) * 0.06} className="break-inside-avoid">
                <button data-testid={`gallery-item-${item.id}`} onClick={() => setZoom(item)} className="group relative block w-full overflow-hidden rounded-sm gold-foil-border">
                  <img
                    src={imgSrc(item.image)}
                    alt={item.title}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ aspectRatio: i % 3 === 0 ? "3/4" : i % 3 === 1 ? "1/1" : "4/5" }}
                  />
                  <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-brand-burgundy/85 via-brand-burgundy/20 to-transparent p-5 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-brand-gold">{item.category}</span>
                    <span className="mt-1 font-serif text-lg text-brand-cream">{item.title}</span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!zoom} onOpenChange={() => setZoom(null)}>
        <DialogContent className="max-w-3xl bg-brand-cream gold-foil-border p-2" data-testid="gallery-lightbox">
          {zoom && (
            <div>
              <img src={imgSrc(zoom.image)} alt={zoom.title} className="max-h-[72vh] w-full rounded-sm object-cover" />
              <div className="p-5">
                <p className="overline-label !text-[10px]">{zoom.category}</p>
                <h3 className="mt-1 font-serif text-xl font-semibold text-brand-burgundy">{zoom.title}</h3>
                {zoom.description && <p className="mt-1 text-sm text-brand-ink-muted">{zoom.description}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
